import { FeatureFlags } from '../generated/feature-flags';
import { useFeatureFlags } from './feature-flags';
import { useAccountData } from './hooks/useAccountData';

/**
 * Name of the Azure App Configuration feature flag that enables the persisted new/old
 * configurator UI choice in Settings.
 *
 * NOTE: this flag must also exist in Azure App Configuration to be remotely toggleable;
 * until then it defaults to OFF (see {@link useConfiguratorUrlSwitchEnabled}).
 */
export const CONFIGURATOR_URL_SWITCH_FLAG: string = 'configuratorUrlSwitch';

/**
 * Adds (or replaces) a single query parameter on a URL, preserving the order and encoding of the
 * other params and any hash. Any existing occurrence of the key (case-insensitive) is removed first
 * so the value is unambiguous. Returns the input unchanged when it is empty.
 */
export const setQueryParam = (url: string | null | undefined, param: string, value: string): string => {
    if (!url) {
        return url ?? '';
    }

    const cleaned = removeQueryParam(url, param);
    const hashIndex = cleaned.indexOf('#');
    const beforeHash = hashIndex >= 0 ? cleaned.slice(0, hashIndex) : cleaned;
    const hash = hashIndex >= 0 ? cleaned.slice(hashIndex) : '';
    const separator = beforeHash.includes('?') ? '&' : '?';
    return `${beforeHash}${separator}${param}=${value}${hash}`;
};

/** Force the new Configurator UI for previews and product Testing Mode. */
export const withForcedNewInterface = (url: string | null | undefined): string => setQueryParam(url, 'forceInterface', 'new');

/**
 * Removes a single query parameter from a URL (case-insensitive on the key), preserving the order and
 * encoding of the remaining params and any hash.
 */
export const removeQueryParam = (url: string | null | undefined, param: string): string => {
    if (!url) {
        return url ?? '';
    }

    const hashIndex = url.indexOf('#');
    const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
    const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const queryIndex = beforeHash.indexOf('?');
    if (queryIndex < 0) {
        return url;
    }

    const base = beforeHash.slice(0, queryIndex);
    const kept = beforeHash
        .slice(queryIndex + 1)
        .split('&')
        .filter((pair) => pair.length > 0 && pair.split('=')[0].toLowerCase() !== param.toLowerCase());
    return `${base}${kept.length > 0 ? `?${kept.join('&')}` : ''}${hash}`;
};

/**
 * Whether the persisted new/old configurator UI choice should be shown for the current user.
 *
 * Gated by the {@link CONFIGURATOR_URL_SWITCH_FLAG} feature flag. Defensive against the flag
 * being absent (e.g. before it has been created in Azure App Configuration, or after the
 * generated flags file is regenerated without it): in that case it returns false, so only
 * the new configurator URL is used.
 */
export const useConfiguratorUrlSwitchEnabled = (): boolean => {
    const { userInfo } = useAccountData();
    const { featureFlags, isEnabledWithConditions } = useFeatureFlags();

    const flagKey = CONFIGURATOR_URL_SWITCH_FLAG as keyof FeatureFlags;
    if (!featureFlags[flagKey]) {
        return false;
    }

    return isEnabledWithConditions(flagKey, { userId: userInfo?.userID?.toString() });
};
